import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Form, Formik } from 'formik'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'

import { i18n } from '../../translate/i18n'

import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  rgbToHex,
  Typography
} from '@material-ui/core'
import { AuthContext } from '../../context/Auth/AuthContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'

import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { colorOptions, getColorConfig, hexToRgb } from './color'
import { InputField } from '../InputField'
import SelectInput from '../SelectInput'
import { useStyles } from './style'
import { QueueDepartmentSelect } from '../QueueDepartmentSelect'

const TagSchema = Yup.object().shape({
  name: Yup.string().min(3, 'Mensagem muito curta').required('Obrigatório'),
  color: Yup.string().required('Obrigatório'),
  visibility: Yup.string()
    .oneOf(['all', 'me', 'department'])
    .required('Obrigatório'),
  queueIds: Yup.array().when('visibility', {
    is: 'department',
    then: schema => schema.min(1, 'Selecione ao menos um departamento'),
    otherwise: schema => schema
  })
})

const TagModal = ({ open, onClose, tagId, reload, kanban }) => {
  const classes = useStyles()
  const { user } = useContext(AuthContext)
  const [loading, setLoading] = useState(false)
  const [selectedColor, setSelectedColor] = useState('#FCE4EC')
  const [selectedTextColor, setSelectedTextColor] = useState('#CA432D')
  const [, setShowEmoji] = useState(false)

  const userQueues = useMemo(() => {
    return (Array.isArray(user?.queues) ? user.queues : []).filter(
      queue =>
        queue.notificationact !== true &&
        queue.UserQueue?.notificationact !== true
    )
  }, [user?.queues])

  const initialState = useMemo(
    () => ({
      name: '',
      color: '#FCE4EC',
      kanban: kanban,
      tipo: 'atendimento',
      visibility: 'all',
      queueIds: []
    }),
    [kanban]
  )

  const [tag, setTag] = useState(initialState)

  useEffect(() => {
    try {
      ;(async () => {
        if (!tagId) {
          setSelectedColor('#FCE4EC')
          return
        }

        const { data } = await api.get(`/tags/${tagId}`)
        data.name = data.name.toUpperCase()

        const queueIds = Array.isArray(data.queues)
          ? data.queues.map(queue => Number(queue.id)).filter(Boolean)
          : []
        const visibility =
          queueIds.length > 0 ? 'department' : data.userId ? 'me' : 'all'

        setTag(prevState => {
          return { ...prevState, ...data, visibility, queueIds }
        })
        if (data.color) {
          setSelectedColor(data.color.toUpperCase())
        }
      })()
    } catch (err) {
      toastError(err)
    }
  }, [tagId, open])

  const handleClose = useCallback(() => {
    setTag(initialState)
    setSelectedColor('#FCE4EC')
    setShowEmoji(false)
    onClose()
  }, [initialState, onClose])

  const handleSaveTag = useCallback(
    async values => {
      const config = getColorConfig(selectedColor)
      const hexColorToSave = rgbToHex(selectedColor)

      const tagData = {
        ...values,
        userId: values.visibility === 'me' ? user.id : null,
        kanban: kanban,
        color: hexColorToSave,
        textColor: config.textColor,
        visibility: values.visibility,
        queueIds: values.queueIds
      }
      try {
        if (tagId) {
          await api.put(`/tags/${tagId}`, tagData)
        } else {
          await api.post('/tags', tagData)
        }
        toast.success(
          kanban === 0
            ? `${i18n.t('tagModal.success')}`
            : `${i18n.t('tagModal.successKanban')}`
        )
        if (typeof reload == 'function') {
          reload()
        }
      } catch (err) {
        toastError(err)
      }
      handleClose()
    },
    [
      user?.id,
      kanban,
      selectedColor,
      selectedTextColor,
      tagId,
      reload,
      handleClose
    ]
  )

  const currentColorConfig = useMemo(
    () => getColorConfig(selectedColor),
    [selectedColor]
  )
  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          {tagId ? `${i18n.t('tagModal.title.edit')}` : 'Nova Tag'}
        </DialogTitle>
        <Formik
          initialValues={tag}
          enableReinitialize={true}
          validationSchema={TagSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveTag(values)
              actions.setSubmitting(false)
            }, 400)
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <InputField
                      label="Nome da Tag"
                      name="name"
                      value={values.name}
                      placeholder="Nome da Tag"
                      onChange={e => {
                        const upperValue = e.target.value.toUpperCase()
                        setFieldValue('name', upperValue)
                        setTag(prev => ({ ...prev, name: upperValue }))
                      }}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      emoji
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" className={classes.label}>
                      Escolha uma cor
                    </Typography>

                    {/* Container do Preview centralizado */}
                    <Box className={classes.previewContainer}>
                      <Box
                        className={classes.previewColor}
                        style={{
                          backgroundColor: selectedColor,
                          borderColor: currentColorConfig.border,
                          color: currentColorConfig.textColor
                        }}
                      >
                        {values.name || 'NOME DA TAG'}
                      </Box>
                    </Box>

                    {/* Grid da paleta de 30 cores */}
                    <Box className={classes.swatchColors}>
                      {colorOptions.map(option => {
                        const isSelected =
                          selectedColor.toUpperCase() ===
                          option.value.toUpperCase()
                        return (
                          <button
                            key={option.value}
                            type="button"
                            aria-label={`Selecionar cor de fundo ${option.value}`}
                            onClick={() => {
                              setSelectedColor(option.value.toUpperCase())
                              setFieldValue('color', option.value.toUpperCase())
                            }}
                            className={classes.colorAdorment}
                            style={{
                              backgroundColor: option.value,
                              border: isSelected
                                ? '3px solid #1976D2'
                                : `1px solid ${option.border}`,
                              transform: isSelected ? 'scale(1.1)' : 'none'
                            }}
                          />
                        )
                      })}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <SelectInput
                      name="tipo"
                      onChange={e =>
                        setTag(prev => ({ ...prev, tipo: e.target.value }))
                      }
                      error={touched.tipo && Boolean(errors.tipo)}
                      helperText={touched.tipo && errors.tipo}
                      label="Tipo da Tag"
                      value={values.tipo || 'atendimento'}
                    >
                      <MenuItem value="atendimento">
                        <Radio
                          checked={values.tipo === 'atendimento'}
                          className={classes.radio}
                        />
                        Atendimento
                      </MenuItem>
                      <MenuItem value="contato">
                        <Radio
                          checked={values.tipo === 'contato'}
                          className={classes.radio}
                        />
                        Contato
                      </MenuItem>
                    </SelectInput>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        Essa tag está ativa para
                      </FormLabel>
                      <div className={classes.optionsContainer}>
                        <FormControlLabel
                          control={
                            <Radio
                              color="primary"
                              checked={values.visibility === 'all'}
                              onChange={() => {
                                setFieldValue('visibility', 'all')
                                setFieldValue('queueIds', [])
                              }}
                              className={classes.radio}
                            />
                          }
                          label="Todos"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              color="primary"
                              checked={values.visibility === 'me'}
                              onChange={() => {
                                setFieldValue('visibility', 'me')
                                setFieldValue('queueIds', [])
                              }}
                              className={classes.radio}
                            />
                          }
                          label="Apenas pra mim"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              color="primary"
                              checked={values.visibility === 'department'}
                              onChange={() =>
                                setFieldValue('visibility', 'department')
                              }
                              className={classes.radio}
                            />
                          }
                          label="Departamento"
                        />
                      </div>
                    </FormControl>
                  </Grid>
                  {values.visibility === 'department' ? (
                    <Grid item xs={12}>
                      <QueueDepartmentSelect
                        label="Departamentos Vinculados"
                        includeAllOption={true}
                        showBots={false}
                        filterByIsBot={true}
                        isMulti={true}
                        disabled={loading}
                        customOptions={userQueues}
                        value={(userQueues || []).filter(queue =>
                          (values.queueIds || []).includes(Number(queue.id))
                        )}
                        onChange={selectedDepartments => {
                          const ids = selectedDepartments
                            .map(d => Number(d.id))
                            .filter(Boolean)
                          setFieldValue('queueIds', ids)
                        }}
                      />

                      {userQueues.length === 0 && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ display: 'block', marginTop: 4 }}
                        >
                          Nenhum departamento vinculado ao seu usuário.
                        </Typography>
                      )}

                      {touched.queueIds && errors.queueIds && (
                        <Typography
                          variant="caption"
                          color="error"
                          style={{ display: 'block', marginTop: 4 }}
                        >
                          {errors.queueIds}
                        </Typography>
                      )}
                    </Grid>
                  ) : null}
                </Grid>
              </DialogContent>

              <DialogActions>
                <ButtonSecondary
                  onClick={handleClose}
                  disabled={isSubmitting}
                  label="CANCELAR"
                />
                <ButtonPrimary
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  label="ADICIONAR"
                />
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  )
}

export default TagModal
